import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { MessageCircle, Star, Trophy, Briefcase, Calendar } from 'lucide-react';

const UserHoverCard = ({ user, children, side = "top", align = "center" }) => {
    const { api, user: currentUser } = useAuth();
    const navigate = useNavigate();
    const [userStats, setUserStats] = useState(null);
    const [loading, setLoading] = useState(false);

    const fetchUserStats = async () => {
        if (!user?.id || userStats) return;
        setLoading(true);
        try {
            const [pointsRes, achievementsRes] = await Promise.all([
                api.get(`/challenges/leaderboard`),
                api.get(`/achievements/user/${user.id}`)
            ]);
            
            const userPoints = pointsRes.data.find(u => u.user_id === user.id);
            setUserStats({
                points: userPoints?.points || 0,
                rank: pointsRes.data.findIndex(u => u.user_id === user.id) + 1,
                achievements: achievementsRes.data.length
            });
        } catch (error) {
            console.error('Error fetching user stats:', error);
        } finally {
            setLoading(false);
        }
    };

    const roleLabels = {
        chef: 'Administrator',
        account_manager: 'Account Manager',
        buchhaltung: 'Buchhaltung',
        mitarbeiter: 'Mitarbeiter'
    };

    const handleStartChat = () => {
        navigate(`/chat?user=${user.id}`);
    };

    if (!user) return children;

    return (
        <HoverCard openDelay={300} closeDelay={100}>
            <HoverCardTrigger asChild onMouseEnter={fetchUserStats}>
                {children}
            </HoverCardTrigger>
            <HoverCardContent 
                side={side} 
                align={align}
                className="w-72 p-0 overflow-hidden"
                data-testid={`user-hover-card-${user.id}`}
            >
                {/* Header with gradient */}
                <div 
                    className="h-16 relative"
                    style={{ 
                        background: `linear-gradient(135deg, ${user.profile_color || '#009dde'} 0%, ${user.profile_color || '#009dde'}80 100%)`
                    }}
                >
                    <div className="absolute -bottom-8 left-4">
                        <Avatar className="w-16 h-16 border-4 border-background shadow-lg">
                            {user.profile_image ? (
                                <AvatarImage src={user.profile_image} alt={user.first_name} />
                            ) : (
                                <AvatarFallback 
                                    style={{ backgroundColor: user.profile_color || '#009dde' }}
                                    className="text-white text-xl font-semibold"
                                >
                                    {user.first_name?.[0]}{user.last_name?.[0]}
                                </AvatarFallback>
                            )}
                        </Avatar>
                    </div>
                </div>
                
                {/* Content */}
                <div className="pt-10 px-4 pb-4">
                    <div className="flex items-start justify-between">
                        <div>
                            <h4 className="font-semibold text-base">
                                {user.first_name} {user.last_name}
                            </h4>
                            <Badge variant="outline" className="mt-1 text-xs">
                                <Briefcase className="w-3 h-3 mr-1" />
                                {user.display_title || roleLabels[user.role] || user.role}
                            </Badge>
                        </div>
                    </div>
                    
                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-2 mt-4">
                        <div className="text-center p-2 rounded-lg bg-muted/50">
                            <Star className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
                            <p className="text-sm font-semibold">
                                {loading ? '...' : userStats?.points || 0}
                            </p>
                            <p className="text-[10px] text-muted-foreground">Punkte</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-muted/50">
                            <Trophy className="w-4 h-4 mx-auto text-orange-500 mb-1" />
                            <p className="text-sm font-semibold">
                                #{loading ? '...' : userStats?.rank || '-'}
                            </p>
                            <p className="text-[10px] text-muted-foreground">Rang</p>
                        </div>
                        <div className="text-center p-2 rounded-lg bg-muted/50">
                            <Trophy className="w-4 h-4 mx-auto text-purple-500 mb-1" />
                            <p className="text-sm font-semibold">
                                {loading ? '...' : userStats?.achievements || 0}
                            </p>
                            <p className="text-[10px] text-muted-foreground">Badges</p>
                        </div>
                    </div>
                    
                    {/* Actions */}
                    {currentUser?.id !== user.id && (
                        <div className="mt-4 flex gap-2">
                            <Button 
                                size="sm" 
                                className="flex-1 gap-2"
                                onClick={handleStartChat}
                                data-testid={`chat-with-${user.id}`}
                            >
                                <MessageCircle className="w-4 h-4" />
                                Nachricht
                            </Button>
                        </div>
                    )}
                </div>
            </HoverCardContent>
        </HoverCard>
    );
};

export default UserHoverCard;
